import React, { createContext, useMemo, useEffect, useState, useContext, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { useIntl, useSelectable, useFileDownload } from 'hooks'
import { PATIENT_FILE_FILTERS } from 'app/constants'
import { useGetSharedFilesWithSummaryQuery } from 'services'
import { CollectionContext } from 'features/surgical-cloud/PatientsCollections'
import { set } from 'features/file-view/fileSlice'
import { Loader } from 'components/Loaders'
import CollectionDetailsHeader from 'components/Collection/CollectionDetailsHeader'
import CollectionDetailsFallback from 'components/Fallback/Details'
import FilesFilter from 'components/FilesUIFilter'
import SelectedFileActions from 'components/SelectedFileActions'
import ThumbnailsContainer from './ThumbnailsContainer'
import { Checkbox, ScrollableDirective } from 'components/Zui'

export const FileContext = createContext(null)

const Details = () => {
    const { t } = useIntl()
    const history = useHistory()
    const reduxDispatch = useDispatch()
    const [filter, setFilter] = useState(null)
    const { collectionId } = useContext(CollectionContext)
    const { download } = useFileDownload('files')

    /*
     *  Data will be cashed for 20 minutes (1200 ms).
     *  To update cache time, change value of `keepUnusedDataFor` in service endpoint
     */
    const { data, isLoading, isFetching, isError } = useGetSharedFilesWithSummaryQuery()
    const summary = data?.summary || { all: 0, image: 0, video: 0 }

    const { actions, dispatch, selectable, handleSelectFile, handleSelectAll, isSelectedAll } =
        useSelectable(data?.files, filter)

    useEffect(() => {
        setFilter('all')
        dispatch(actions.unSelectAll())
    }, [collectionId])

    const getFilteredFileIds = useCallback(() => {
        let fileIds = []
        if (filter !== 'all') {
            fileIds = data?.files.filter(t => t.fileType === filter).map(t => t.fileId) || []
        } else {
            fileIds = data?.files.map(f => f.fileId) || []
        }
        return fileIds
    }, [filter, data])

    const handleFileView = useCallback(
        id => {
            const fileIds = getFilteredFileIds()
            const viewFiles = data?.files.filter(f => fileIds.includes(f.fileId)) || []

            reduxDispatch(set(viewFiles))
            history.push({
                pathname: '/files/' + id,
                key: id,
                state: {
                    actionType: 'file-view',
                    tab: 'collections',
                    id: 'shared',
                    filter,
                },
            })
        },
        [filter, data]
    )

    const handleDownloadFiles = () => {
        const fileIds = selectable.selectedFiles
        const downloadableFileIds = data?.files
            .filter(f => fileIds.includes(f.fileId) && f.isDownloadable)
            .map(f => f.fileId)

        download({
            extraOptions: { isShared: true },
            fileIds: downloadableFileIds,
        })
    }

    const canDownload = useMemo(() => {
        const fileIds = selectable.selectedFiles
        const filtered = data?.files.filter(f => fileIds.includes(f.fileId))
        return Boolean(fileIds.length) && filtered?.some(f => f.isDownloadable)
    }, [selectable])

    const filterOptions = PATIENT_FILE_FILTERS.map(op => {
        return { ...op, count: summary[op.value] || 0 }
    })

    const RenderThumbnailsContainer = useMemo(
        () => (
            <div className="treatment-details-wrapper">
                <ScrollableDirective style={{ height: '100%' }}>
                    {data?.grouped.map((d, index) => (
                        <ThumbnailsContainer
                            data={d}
                            key={index}
                            defaultOpen={index === 0}
                            filter={filter}
                            files={d.files}
                            handleSelectFile={handleSelectFile}
                            handleClick={handleFileView}
                        />
                    ))}
                </ScrollableDirective>
            </div>
        ),
        [collectionId, data, filter, selectable]
    )

    return (
        <FileContext.Provider value={{ selectable, actions, dispatch }}>
            <div className="content-container">
                <div className="patient-details-wrapper">
                    {collectionId ? (
                        <>
                            {isLoading && <Loader />}
                            {!isLoading && !isFetching && (!data?.summary.all || isError) && (
                                <CollectionDetailsFallback
                                    title={t('fallback.no.media')}
                                    icon="image"
                                />
                            )}

                            {data?.summary.all > 0 && (
                                <>
                                    <div className="patient-contents-header">
                                        <CollectionDetailsHeader
                                            data={{
                                                name: 'Shared with me',
                                                fileCount: summary.all || 0,
                                                updatedDateTime: data?.summary.shareDate,
                                            }}
                                        />
                                        <div className="filter-select-options-wrapper">
                                            <div className="files-filter-wrapper">
                                                <FilesFilter
                                                    isDisabled={!Boolean(summary.all)}
                                                    value={filter}
                                                    onChange={setFilter}
                                                    options={filterOptions}
                                                />
                                                <Checkbox
                                                    style={{ display: ' flex', fontSize: ' 14px' }}
                                                    theme="zbds-dark"
                                                    name="selectAll"
                                                    disabled={!Boolean(summary.all)}
                                                    value={isSelectedAll}
                                                    onChange={e => handleSelectAll(e.target.value)}
                                                >
                                                    <span style={{ color: '#D1D9E5' }}>
                                                        {t('select.all')}
                                                    </span>
                                                </Checkbox>
                                            </div>
                                            {Boolean(
                                                selectable.selectedFiles.length && summary[filter]
                                            ) && (
                                                <SelectedFileActions
                                                    actions={[
                                                        {
                                                            icon: 'download',
                                                            onClick: handleDownloadFiles,
                                                            isDisabled: !canDownload,
                                                        },
                                                    ]}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    {RenderThumbnailsContainer}
                                </>
                            )}
                        </>
                    ) : (
                        <CollectionDetailsFallback
                            title={t('fallback.no.collection.selected')}
                            icon="folder"
                        />
                    )}
                </div>
            </div>
        </FileContext.Provider>
    )
}

export default React.memo(Details)
